import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import CodeEditor from '@uiw/react-textarea-code-editor';
import { QRCodeSVG } from 'qrcode.react';
import { 
  Copy, 
  Download, 
  Share2, 
  Lock,
  Clock,
  FileCode,
  ArrowLeft,
  Eye,
  Code,
  AlertTriangle,
  CheckCircle,
  FileDown,
  Image as ImageIcon,
  Star,
  ChevronDown,
  Printer,
  QrCode,
  X,
  Twitter,
  Facebook,
  Linkedin,
  Mail,
  BookOpen,
  Flame,
  FileText,
  ThumbsUp,
  ThumbsDown,
  User,
  Globe,
  Edit3
} from 'lucide-react';
import toast from 'react-hot-toast';
import { getPaste, unlockPaste, votePaste } from '../utils/api';
import { hashPassword } from '../utils/storage';
import { languages, getFileExtension } from '../utils/languages';
import { exportToPDF, exportToHTML } from '../utils/export';
import { addToFavorites, isFavorite as checkFavorite } from '../utils/history';
import { updatePasteSEO } from '../hooks/useSEO';
import BackButton from './BackButton';

const PublishedViewer = ({ theme }) => {
  const { shortId } = useParams();
  const navigate = useNavigate();
  const [paste, setPaste] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLocked, setIsLocked] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [showRaw, setShowRaw] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isFav, setIsFav] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [votes, setVotes] = useState({ up: 0, down: 0 });
  const [userVote, setUserVote] = useState(null); // 'up', 'down', or null
  const [stars, setStars] = useState(0);
  const [hasStarred, setHasStarred] = useState(false);
  
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Use ref to prevent double-fetching (especially important for burn-after-read)
  const hasFetched = useRef(false);

  useEffect(() => {
    // Prevent double fetch
    if (hasFetched.current) return;
    
    const fetchPaste = async () => {
      hasFetched.current = true;
      try {
        setLoading(true);
        
        // Check if this user is the author (just created this paste)
        const isAuthor = sessionStorage.getItem(`author_${shortId}`) === 'true';
        // Clear the flag after checking (only skip once)
        if (isAuthor) {
          sessionStorage.removeItem(`author_${shortId}`);
        }
        
        const data = await getPaste(shortId, isAuthor);
        
        if (data.isPasswordProtected) {
          setIsLocked(true);
          setPaste(data); // Partial data (no content)
        } else {
          setPaste(data);
          // Update SEO meta tags for the paste
          updatePasteSEO(data);
          // Initialize votes from server data
          setVotes({ up: data.upvotes || 0, down: data.downvotes || 0 });
          setStars(data.upvotes || 0); // Stars can mirror upvotes or be separate
          checkFavorite(shortId).then(setIsFav);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (shortId) {
      fetchPaste();
    }
  }, [shortId]);

  const handleUnlock = async () => {
    if (!passwordInput) {
      toast.error('Please enter a password');
      return;
    }

    setVerifying(true);
    try {
      const hashedPassword = await hashPassword(passwordInput);
      const data = await unlockPaste(shortId, hashedPassword);
      setPaste(data);
      // Update SEO meta tags for the unlocked paste
      updatePasteSEO(data);
      setIsLocked(false);
      // Initialize votes from server data
      setVotes({ up: data.upvotes || 0, down: data.downvotes || 0 });
      setStars(data.upvotes || 0);
      toast.success('Paste unlocked!');
      checkFavorite(shortId).then(setIsFav);
    } catch (err) {
      toast.error(err.message || 'Incorrect password');
    } finally {
      setVerifying(false);
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(paste.content);
    toast.success('Content copied to clipboard!');
  };

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(window.location.href);
    toast.success('Share link copied!');
  };

  const handleDownload = () => {
    const blob = new Blob([paste.content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${paste.title || 'scrawl'}.${getFileExtension(paste.language)}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('File downloaded!');
  };

  const handleExportPDF = async () => {
    try {
      toast.loading('Generating PDF...');
      await exportToPDF(paste.content, paste.title, { 
        language: paste.language, 
        theme 
      });
      toast.dismiss();
      toast.success('PDF exported successfully!');
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to export PDF');
    }
    setShowExportMenu(false);
  };

  const handleExportHTML = () => {
    exportToHTML(paste.content, paste.title, { 
      language: paste.language, 
      theme 
    });
    toast.success('HTML file downloaded!');
    setShowExportMenu(false);
  };

  const handleAddToFavorites = async () => {
    await addToFavorites({ ...paste, id: shortId });
    setIsFav(true);
    toast.success('Added to favorites!');
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${paste.title || 'Scrawl'}</title>
          <style>
            body { font-family: 'Segoe UI', sans-serif; padding: 2rem; max-width: 800px; margin: 0 auto; }
            pre { background: #f5f5f5; padding: 1rem; overflow-x: auto; }
            h1 { border-bottom: 2px solid #333; padding-bottom: 0.5rem; }
          </style>
        </head>
        <body>
          <h1>${paste.title || 'Untitled'}</h1>
          ${paste.editorMode === 'rich' ? paste.content : `<pre><code>${paste.content.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</code></pre>`}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Handle edit - navigate to editor with paste data
  const handleEdit = () => {
    if (!paste) return;
    
    // Store paste data in sessionStorage for the editor to pick up
    const editData = {
      title: paste.title || '',
      content: paste.content || '',
      language: paste.language || 'plaintext',
      editorMode: paste.editorMode || 'code',
      images: paste.images || [],
      originalShortId: shortId, // Keep reference to original
    };
    
    sessionStorage.setItem('scrawl_edit_paste', JSON.stringify(editData));
    
    toast.success('Opening in editor...', {
      style: {
        background: '#0f3460',
        color: '#eaeaea',
        border: '3px solid #0eff9f',
        fontFamily: 'JetBrains Mono, monospace',
        fontWeight: '600'
      }
    });
    
    navigate('/');
  };

  const getReadingTime = () => {
    if (!paste?.content) return '< 1 min';
    const text = paste.content.replace(/<[^>]*>/g, '');
    const words = text.trim().split(/\s+/).filter(w => w.length > 0).length;
    const minutes = Math.ceil(words / 200);
    return minutes < 1 ? '< 1 min' : `${minutes} min read`;
  };

  const getWordCount = () => {
    if (!paste?.content) return 0;
    const text = paste.content.replace(/<[^>]*>/g, '');
    return text.trim().split(/\s+/).filter(w => w.length > 0).length;
  };

  const shareToSocial = (platform) => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(paste.title || 'Check out my paste on Scrawl');
    
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      email: `mailto:?subject=${text}&body=${url}`
    };
    
    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Format time ago (e.g., "5s", "3m", "2h", "1d")
  const getTimeAgo = (dateString) => {
    const now = new Date();
    const created = new Date(dateString);
    const diffMs = now - created;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 60) return `${diffSec}s`;
    if (diffMin < 60) return `${diffMin}m`;
    if (diffHour < 24) return `${diffHour}h`;
    return `${diffDay}d`;
  };

  // Format time remaining until expiration
  const getTimeRemaining = (expiresAt) => {
    if (!expiresAt) return null;
    const now = new Date();
    const expires = new Date(expiresAt);
    const diffMs = expires - now;
    
    if (diffMs <= 0) return 'Expired';
    
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffDay > 0) return `${diffDay}d ${diffHour % 24}h`;
    if (diffHour > 0) return `${diffHour}h ${diffMin % 60}m`;
    if (diffMin > 0) return `${diffMin}m`;
    return `${diffSec}s`;
  };

  // Handle voting
  const handleVote = async (type) => {
    const voteKey = `vote_${shortId}`;
    const existingVote = localStorage.getItem(voteKey);
    
    try {
      if (existingVote === type) {
        // Remove vote
        await votePaste(shortId, type, 'remove');
        localStorage.removeItem(voteKey);
        setUserVote(null);
        setVotes(prev => ({ ...prev, [type]: Math.max(0, prev[type] - 1) }));
      } else {
        // Change or add vote
        if (existingVote) {
          // Remove old vote first
          await votePaste(shortId, existingVote, 'remove');
          setVotes(prev => ({ ...prev, [existingVote]: Math.max(0, prev[existingVote] - 1) }));
        }
        // Add new vote
        await votePaste(shortId, type, 'add');
        localStorage.setItem(voteKey, type);
        setUserVote(type);
        setVotes(prev => ({ ...prev, [type]: prev[type] + 1 }));
      }
    } catch (error) {
      toast.error('Failed to vote');
    }
  };

  // Handle starring
  const handleStar = () => {
    const starKey = `star_${shortId}`;
    if (hasStarred) {
      localStorage.removeItem(starKey);
      setHasStarred(false);
      setStars(prev => Math.max(0, prev - 1));
    } else {
      localStorage.setItem(starKey, 'true');
      setHasStarred(true);
      setStars(prev => prev + 1);
    }
  };
  // Load votes and stars on mount
  useEffect(() => {
    if (shortId) {
      const voteKey = `vote_${shortId}`;
      const starKey = `star_${shortId}`;
      setUserVote(localStorage.getItem(voteKey));
      setHasStarred(!!localStorage.getItem(starKey));
    }
  }, [shortId]);

  const getLanguageLabel = (value) => {
    const lang = languages.find(l => l.value === value);
    return lang ? lang.label : 'Plain Text';
  };

  if (loading) {
    return (
      <div className="viewer-loading">
        <div className="spinner"></div>
        <p>Loading paste...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="viewer-error">
        <AlertTriangle size={48} />
        <h2>Oops!</h2>
        <p>{error}</p>
        <Link to="/" className="action-btn primary">
          <ArrowLeft size={16} />
          Create New Paste
        </Link>
      </div>
    );
  }

  if (isLocked) {
    return (
      <div className="viewer-locked">
        <Lock size={48} />
        <h2>This paste is password protected</h2>
        <p>Enter the password to view the content</p>
        <div className="unlock-form">
          <input
            type="password"
            placeholder="Enter password..."
            value={passwordInput}
            onChange={(e) => setPasswordInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleUnlock()}
            className="password-input"
          />
          <button 
            className="action-btn primary" 
            onClick={handleUnlock}
            disabled={verifying}
          >
            {verifying ? 'Verifying...' : 'Unlock'}
          </button>
        </div>
        <Link to="/" className="back-link">
          <ArrowLeft size={16} />
          Create New Paste
        </Link>
      </div>
    );
  }

  const lineCount = paste?.content?.split('\n').length || 0;
  const currentUrl = window.location.href;

  return (
    <div className="viewer-container">
      <div className="viewer-back-row">
        <BackButton />
      </div>
      {/* Author & Status Bar - JustPaste.it style */}
      <div className="paste-author-bar">
        <div className="author-info">
          <div className="author-avatar">
            <User size={24} />
          </div>
          <div className="author-details">
            <span className="author-name">@{paste.authorUsername || 'anonymous'}</span>
            <span className="post-time">· {getTimeAgo(paste.createdAt)}</span>
          </div>
        </div>
        <div className="paste-status">
          <span className="status-badge unlisted">
            <Globe size={12} />
            Unlisted
          </span>
          {paste.expiresAt && (
            <span className={`status-badge expiration ${getTimeRemaining(paste.expiresAt) === 'Expired' ? 'expired' : ''}`}>
              <Clock size={12} />
              {getTimeRemaining(paste.expiresAt) === 'Expired' ? 'Expired' : `Expires in ${getTimeRemaining(paste.expiresAt)}`}
            </span>
          )}
          {paste.burnAfterRead && (
            <span className="status-badge burn">
              <Flame size={12} />
              Burns after read
            </span>
          )}
          <button type="button" className="edit-btn" title="Edit paste" onClick={handleEdit}>
            <Edit3 size={14} />
            Edit
          </button>
        </div>
      </div>

      {/* Title */}
      <div className="viewer-header">
        <div className="viewer-info">
          <h1 className="viewer-title">{paste.title || 'Untitled'}</h1>
        </div>
      </div>

      {/* Images */}
      {paste.images?.length > 0 && (
        <div className="viewer-images">
          <div className="viewer-images-header">
            <ImageIcon size={16} />
            <span>Attached Images ({paste.images.length})</span>
          </div>
          <div className="images-grid">
            {paste.images.map((img, index) => (
              <div key={img.id || index} className="viewer-image">
                <img 
                  src={img.url} 
                  alt={img.name || `Image ${index + 1}`} 
                  onClick={() => window.open(img.url, '_blank')}
                />
                <div className="image-caption">
                  {img.name || `Image ${index + 1}`}
                  {img.size && <span className="file-size">{img.size}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="viewer-content">
        {paste.editorMode === 'rich' && !showRaw ? (
          <div 
            className="rich-content-view"
            dangerouslySetInnerHTML={{ __html: paste.content }}
          />
        ) : (
          <div className="code-content-view">
            <div className="line-numbers">
              {Array.from({ length: lineCount }, (_, i) => (
                <span key={i + 1}>{i + 1}</span>
              ))}
            </div>
            <CodeEditor
              value={paste.content}
              language={paste.language}
              readOnly
              data-color-mode={theme}
              style={{
                flex: 1,
                fontSize: 14,
                fontFamily: '"JetBrains Mono", "Fira Code", "Consolas", monospace',
                backgroundColor: 'transparent',
              }}
            />
          </div>
        )}
      </div>

      {/* Footer - JustPaste.it style with voting */}
      <div className="paste-footer-bar">
        <div className="footer-left">
          <span className="visit-count">
            <Eye size={14} />
            {paste.viewCount} visit{paste.viewCount !== 1 ? 's' : ''}
          </span>
        </div>
        
        <div className="footer-right">
          <div className="vote-buttons">
            <span className="vote-label">Vote:</span>
            <button 
              type="button" 
              className={`vote-btn upvote ${userVote === 'up' ? 'active' : ''}`}
              onClick={() => handleVote('up')}
              title="Upvote"
            >
              <ThumbsUp size={14} />
              <span>{votes.up}</span>
            </button>
            <button 
              type="button" 
              className={`vote-btn downvote ${userVote === 'down' ? 'active' : ''}`}
              onClick={() => handleVote('down')}
              title="Downvote"
            >
              <ThumbsDown size={14} />
              <span>{votes.down}</span>
            </button>
          </div>
          
          <button 
            type="button" 
            className={`star-btn ${hasStarred ? 'active' : ''}`}
            onClick={handleStar}
            title="Star this paste"
          >
            <Star size={14} fill={hasStarred ? 'currentColor' : 'none'} />
            <span>{stars}</span>
          </button>
          
          <button type="button" className="action-btn-small" onClick={handleExportPDF}>
            <FileDown size={14} />
            Save as PDF
          </button>
          
          <button type="button" className="action-btn-small share" onClick={() => setShowQRModal(true)}>
            <Share2 size={14} />
            Share
          </button>
        </div>
      </div>

      {/* QR & Share Modal */}
      {showQRModal && (
        <div className="modal-overlay" onClick={() => setShowQRModal(false)}>
          <div className="modal-content qr-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="modal-close" onClick={() => setShowQRModal(false)}>
              <X size={20} />
            </button>
            <h2>Share This Paste</h2>
            {currentUrl.length <= 2000 ? (
              <div className="qr-code-container">
                <QRCodeSVG value={currentUrl} size={200} level="L" />
              </div>
            ) : (
              <div className="qr-unavailable">
                <QrCode size={48} />
                <p>QR code unavailable</p>
              </div>
            )}
            <p className="share-url">{currentUrl}</p>
            <div className="modal-buttons-row">
              <button 
                type="button" 
                className="action-btn secondary copy-url-btn"
                onClick={handleCopyLink}
              >
                <Copy size={16} />
                Copy Link
              </button>
            </div>
            <div className="social-share-buttons">
              <button type="button" className="social-btn twitter" onClick={() => shareToSocial('twitter')} title="Share on Twitter">
                <Twitter size={20} />
              </button>
              <button type="button" className="social-btn facebook" onClick={() => shareToSocial('facebook')} title="Share on Facebook">
                <Facebook size={20} />
              </button>
              <button type="button" className="social-btn linkedin" onClick={() => shareToSocial('linkedin')} title="Share on LinkedIn">
                <Linkedin size={20} />
              </button>
              <button type="button" className="social-btn email" onClick={() => shareToSocial('email')} title="Share via Email">
                <Mail size={20} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublishedViewer;
